import { Button, Menu } from "@mantine/core";
import {
  IconCheckOutline18,
  IconCopyOutline18,
  IconFileOutline18,
  IconLinkOutline18,
  IconRobotOutline18,
} from "nucleo-ui-outline-18";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ICON_SIZE } from "@mountayaapp/theme";

import { DOCS_ORIGIN } from "../config/endpoints";
import { buildMarkdownHref } from "../content/href";
import { entryToMarkdown } from "../content/markdown";
import type { DocEntry } from "../content/registry";
import { resolveDoc } from "../content/resolveDoc";

/**
 * LlmActions is the resolved set of "take this page for an LLM" affordances for a
 * pathname: the page entry itself, the URL of its clean-Markdown twin, and the
 * absolute page URL. It is `null` for a pathname that resolves to no page.
 */
export interface LlmActions {
  entry: DocEntry;
  markdownHref: string;
  pageUrl: string;
}

/**
 * resolveLlmActions matches a pathname to its page and derives the LLM affordances
 * from it, reusing the same pathname-to-page resolution the rest of the chrome
 * uses. It returns `null` when no page is registered at the pathname, so the
 * control renders nothing on a route with no page (the not-found view). The origin
 * defaults to the public docs origin and is injectable for tests.
 */
export const resolveLlmActions = (
  pathname: string,
  origin: string = DOCS_ORIGIN,
): LlmActions | null => {
  const { doc } = resolveDoc(pathname);
  if (!doc) {
    return null;
  }

  return {
    entry: doc,
    markdownHref: buildMarkdownHref(doc.href),
    pageUrl: `${origin}${doc.href}`,
  };
};

/**
 * COPIED_RESET_MS is how long the control confirms a copy before returning to its
 * idle label.
 */
const COPIED_RESET_MS = 2000;

/**
 * DocsLlmActionsProps carry the active pathname, from which the control resolves
 * the current page and its Markdown twin.
 */
interface DocsLlmActionsProps {
  pathname: string;
}

/**
 * DocsLlmActions is the per-page control that lets a reader take the page for a
 * language model: a menu offering the page as clean Markdown copied to the
 * clipboard, the same Markdown opened at its `.md` URL, or the page URL copied.
 * The clean Markdown is produced on demand from the already-parsed content entry,
 * so nothing is fetched. Clipboard access is reached only inside the click
 * handlers and guarded, so the control renders safely during the static prerender
 * where no clipboard exists, and it renders nothing on a route with no page.
 */
export const DocsLlmActions = ({ pathname }: DocsLlmActionsProps) => {
  const { t } = useTranslation("docs");
  const [copied, setCopied] = useState(false);
  const actions = resolveLlmActions(pathname);

  useEffect(() => {
    if (!copied) {
      return;
    }

    const timer = window.setTimeout(() => setCopied(false), COPIED_RESET_MS);

    return () => window.clearTimeout(timer);
  }, [copied]);

  if (!actions) {
    return null;
  }

  const { entry, markdownHref, pageUrl } = actions;

  const writeClipboard = async (text: string): Promise<void> => {
    if (typeof navigator === "undefined" || !navigator.clipboard) {
      return;
    }

    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      // Clipboard access can be denied by the browser; leave the control idle
      // rather than surfacing an error for a convenience action.
    }
  };

  return (
    <Menu position="bottom-end" shadow="md" width={240} withinPortal>
      <Menu.Target>
        <Button
          aria-label={t("llm.ariaLabel")}
          leftSection={
            copied ? (
              <IconCheckOutline18 size={ICON_SIZE.xs} />
            ) : (
              <IconRobotOutline18 size={ICON_SIZE.xs} />
            )
          }
          variant="default"
        >
          {copied ? t("llm.copied") : t("llm.copyForLlm")}
        </Button>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item
          leftSection={<IconCopyOutline18 size={ICON_SIZE.xs} />}
          onClick={() => void writeClipboard(entryToMarkdown(entry))}
        >
          {t("llm.copyMarkdown")}
        </Menu.Item>
        <Menu.Item
          component="a"
          href={markdownHref}
          leftSection={<IconFileOutline18 size={ICON_SIZE.xs} />}
          rel="noopener noreferrer"
          target="_blank"
        >
          {t("llm.viewMarkdown")}
        </Menu.Item>
        <Menu.Item
          leftSection={<IconLinkOutline18 size={ICON_SIZE.xs} />}
          onClick={() => void writeClipboard(pageUrl)}
        >
          {t("llm.copyUrl")}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};
